/**
 * ServiceTeamBand — a brand-tinted "Meet the team" section rendered on a
 * service detail page. Shows the 5-member team in service-branded shirts,
 * a 3-photo gallery strip, and a small "judgment stays with people"
 * disclaimer.
 *
 * Currently used on the JapanETCcard detail page; can be reused on any
 * detail page that has its own branded team assets.
 *
 * Props:
 *   - lang: "en" | "ja"
 *   - accent: brand hex
 *   - softBg: brand light tint
 *   - team: array of { slug, portrait, nameEn, nameJa, titleEn, titleJa, role, bioEn, bioJa }
 *   - groupPhotos: array of { id, src, locationEn, locationJa }
 *   - manifesto: { eyebrowEn, eyebrowJa, titleEn, titleJa, leadEn, leadJa }
 *   - slug: e.g. "japanetccard" (used for data-testid surface)
 */
import { useEffect, useState } from "react";
import { Sparkles, ShieldCheck } from "lucide-react";

import Reveal from "@/components/motion/Reveal";

function pickL(en, ja, lang) {
  return lang === "ja" ? ja : en;
}

export default function ServiceTeamBand({
  lang,
  accent,
  softBg,
  team,
  groupPhotos,
  manifesto,
  slug,
}) {
  const isJa = lang === "ja";
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (!groupPhotos || groupPhotos.length <= 1) return undefined;
    const id = setInterval(() => {
      setActive((p) => (p + 1) % groupPhotos.length);
    }, 5000);
    return () => clearInterval(id);
  }, [groupPhotos]);

  if (!team || team.length === 0) return null;

  const photo = groupPhotos?.[active];

  return (
    <section
      className="border-b border-[#E2E8F0]"
      style={{ backgroundColor: softBg }}
      data-testid={`service-detail-${slug}-team-band`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 md:py-20">
        {/* Top: eyebrow + headline + lead */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-end mb-10 md:mb-12">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 mb-3">
              <span
                aria-hidden
                className="h-1.5 w-1.5"
                style={{ backgroundColor: accent }}
              />
              <span
                className="text-[10px] tracking-[0.28em] font-mono-display uppercase font-bold"
                style={{ color: accent }}
              >
                {pickL(manifesto.eyebrowEn, manifesto.eyebrowJa, lang)}
              </span>
            </div>
            <h2
              className={[
                isJa ? "font-ja" : "font-display",
                "text-[#0F1D2D] text-2xl md:text-[36px] font-bold tracking-tighter leading-[1.1]",
              ].join(" ")}
            >
              {pickL(manifesto.titleEn, manifesto.titleJa, lang)}
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-[14.5px] leading-[1.85] text-[#475569]">
              {pickL(manifesto.leadEn, manifesto.leadJa, lang)}
            </p>
          </div>
        </div>

        {/* Hero group photo marquee */}
        {groupPhotos && groupPhotos.length > 0 && (
          <Reveal as="div" variant="up" className="mb-10 md:mb-14">
            <div
              className="relative w-full aspect-[16/9] md:aspect-[21/9] rounded-2xl overflow-hidden ring-1 ring-[#E2E8F0] bg-white"
              data-testid={`service-detail-${slug}-team-hero`}
            >
              {groupPhotos.map((p, i) => (
                <img
                  key={p.id}
                  src={p.src}
                  alt={p.captionEn || `${slug} team — NoJo Enterprise`}
                  className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
                  style={{ opacity: i === active ? 1 : 0 }}
                  loading={i === 0 ? "eager" : "lazy"}
                />
              ))}
              <div
                aria-hidden
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(180deg, transparent 50%, rgba(15,29,45,0.78) 100%)`,
                }}
              />
              <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 backdrop-blur-sm text-white ring-1 ring-white/20">
                  <span
                    aria-hidden
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: accent }}
                  />
                  <span className="text-[10.5px] tracking-[0.22em] font-mono-display uppercase font-bold">
                    {photo
                      ? pickL(photo.locationEn, photo.locationJa, lang)
                      : ""}
                  </span>
                </div>
                <div className="flex items-center gap-1.5">
                  {groupPhotos.map((p, i) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setActive(i)}
                      aria-label={pickL(p.locationEn, p.locationJa, lang)}
                      data-testid={`service-detail-${slug}-team-dot-${p.id}`}
                      className={[
                        "h-1.5 rounded-full transition-all",
                        i === active
                          ? "w-7 bg-white"
                          : "w-1.5 bg-white/45 hover:bg-white/70",
                      ].join(" ")}
                    />
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        )}

        {/* 5-member portrait row */}
        <ul
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4"
          data-testid={`service-detail-${slug}-team-members`}
        >
          {team.map((m, i) => (
            <Reveal as="li" variant="up" delay={i * 70} key={m.slug}>
              <div
                className="h-full rounded-2xl bg-white ring-1 ring-[#E2E8F0] overflow-hidden transition-all hover:-translate-y-0.5 hover:ring-transparent hover:shadow-[0_18px_40px_-20px_rgba(15,29,45,0.3)]"
                data-testid={`service-detail-${slug}-team-member-${m.slug}`}
              >
                <div className="relative aspect-square w-full overflow-hidden bg-[#F1F5F9]">
                  <img
                    src={m.portrait}
                    alt={`${pickL(m.nameEn, m.nameJa, lang)} — ${pickL(m.titleEn, m.titleJa, lang)}`}
                    className="block w-full h-full object-cover"
                    loading="lazy"
                    draggable={false}
                  />
                  <span
                    aria-hidden
                    className="absolute top-0 left-0 right-0 h-[3px]"
                    style={{ backgroundColor: accent }}
                  />
                </div>
                <div className="p-3 md:p-4">
                  <div className="text-[14px] font-bold tracking-tight text-[#0F1D2D] leading-snug">
                    {pickL(m.nameEn, m.nameJa, lang)}
                  </div>
                  <div
                    className="mt-0.5 text-[10.5px] tracking-[0.16em] font-mono-display uppercase font-semibold leading-snug"
                    style={{ color: accent }}
                  >
                    {pickL(m.titleEn, m.titleJa, lang)}
                  </div>
                  <p className="mt-2 text-[11.5px] leading-[1.7] text-[#475569] line-clamp-4">
                    {pickL(m.bioEn, m.bioJa, lang)}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </ul>

        {/* AI-discipline note */}
        <div
          className="mt-8 md:mt-10 flex items-start gap-3 rounded-xl bg-white ring-1 ring-[#E2E8F0] p-4 md:p-5"
          data-testid={`service-detail-${slug}-team-disclaimer`}
        >
          <ShieldCheck
            className="h-5 w-5 shrink-0 mt-0.5"
            style={{ color: accent }}
            strokeWidth={1.75}
          />
          <p className="text-[12.5px] leading-[1.75] text-[#475569]">
            {isJa
              ? "AIによる支援は判断を保証するものではありません。お客様への影響がある判断はすべて、必ず人によるレビューを経由します。"
              : "AI assists with extraction and verification — it does not guarantee outcomes. Anything that affects a customer is reviewed by a person before it leaves the team."}
          </p>
          <Sparkles
            className="h-4 w-4 shrink-0 mt-0.5 hidden md:block"
            style={{ color: accent }}
            strokeWidth={2}
          />
        </div>
      </div>
    </section>
  );
}
